import Phaser from 'phaser';
import { COLORS, GAME_WIDTH, GAME_HEIGHT, ASSET_BASE } from '../constants.js';

export default class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload() {
    // Set base URL for all assets (handles /deadsector/ prefix in production)
    this.load.setBaseURL(ASSET_BASE);
    // Create 80s-style loading bar
    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();

    progressBox.fillStyle(0x222222, 0.8);
    progressBox.fillRect(GAME_WIDTH / 2 - 160, GAME_HEIGHT / 2 - 25, 320, 50);

    // Loading text with retro font
    const loadingText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, 'LOADING...', {
      fontFamily: 'Courier New',
      fontSize: '24px',
      fill: '#ff00ff',
    });
    loadingText.setOrigin(0.5, 0.5);

    // Percentage text
    const percentText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '0%', {
      fontFamily: 'Courier New',
      fontSize: '18px',
      fill: '#00ffff',
    });
    percentText.setOrigin(0.5, 0.5);

    // Asset loading text
    const assetText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 50, '', {
      fontFamily: 'Courier New',
      fontSize: '14px',
      fill: '#ffffff',
    });
    assetText.setOrigin(0.5, 0.5);

    // Update progress bar
    this.load.on('progress', (value) => {
      progressBar.clear();
      progressBar.fillStyle(COLORS.NEON_PINK, 1);
      progressBar.fillRect(GAME_WIDTH / 2 - 150, GAME_HEIGHT / 2 - 15, 300 * value, 30);
      percentText.setText(`${Math.floor(value * 100)}%`);
    });

    this.load.on('fileprogress', (file) => {
      assetText.setText(`Loading: ${file.key}`);
    });

    this.load.on('complete', () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
      percentText.destroy();
      assetText.destroy();
    });

    // Load actual sprite sheets
    this.load.spritesheet('hero_sheet', 'assets/sprites/player/hero_0.png', {
      frameWidth: 256,
      frameHeight: 280,
    });

    this.load.spritesheet('hero_actions', 'assets/sprites/player/hero_1.png', {
      frameWidth: 256,
      frameHeight: 280,
    });

    // Load tank sprite (306x442, points up by default)
    this.load.image('tank', 'assets/sprites/player/tank.png');

    this.load.image('scientist_sprite', 'assets/sprites/npcs/scientist_transparent.png');

    // Load title screen assets
    this.load.image('title_screen', 'assets/Dead-Sector-load-screen.png');
    this.load.audio('theme_music', 'assets/audio/music/Midnight-Error-Detected.mp3');
    this.load.audio('cutscene_music', 'assets/audio/music/Patient Zero Escape.mp3');
    this.load.audio('gameplay_music', 'assets/audio/music/Midnight-Reactor.mp3');
    this.load.audio('victory_music', 'assets/audio/music/Victory End Screen.mp3');

    // Load tile assets as spritesheets where applicable
    // Load the cleaned road tileset (192x192)
    this.load.spritesheet('road_tiles_clean', 'assets/tiles/road_tiles_clean.png', { frameWidth: 192, frameHeight: 192 });
    
    this.load.spritesheet('tile_sidewalk', 'assets/tiles/sidewalk_tiles.png', { frameWidth: 32, frameHeight: 32 });
    // this.load.spritesheet('tile_building', 'assets/tiles/building_ruins.png', { frameWidth: 32, frameHeight: 32 });
    
    // Load new individual building sprites (generation 1 - from buildings_split)
    for (let i = 0; i < 8; i++) {
        this.load.image(`building_${i}`, `/assets/tiles/buildings_split/building_${i}.png`);
    }

    // Load isometric building sprites (generation 2 - from buildings_isometric_2)
    this.load.image('building_apartment', 'assets/tiles/buildings_isometric_2_sprites/building_apartment_2_0.png');
    this.load.image('building_destroyed_skyscraper', 'assets/tiles/buildings_isometric_2_sprites/building_destroyed-skyscraper_2_0.png');
    this.load.image('building_grocery_store', 'assets/tiles/buildings_isometric_2_sprites/building_grocery-store_2_0.png');
    this.load.image('building_hospital', 'assets/tiles/buildings_isometric_2_sprites/building_hospital_2_0.png');
    this.load.image('building_hotel', 'assets/tiles/buildings_isometric_2_sprites/building_hotel_2_0.png');
    this.load.image('building_parking_garage', 'assets/tiles/buildings_isometric_2_sprites/building_parking-garage_2_0.png');
    this.load.image('building_school', 'assets/tiles/buildings_isometric_2_sprites/building_school_2_0.png');
    this.load.image('building_subway_entrance', 'assets/tiles/buildings_isometric_2_sprites/building_subway-entrance_2_0.png');

    // Load zombie gameplay sprites
    this.load.spritesheet('zombie_shambler_walk', 'assets/sprites/gameplay/zombie_shambler_walk.png', {
      frameWidth: 200,
      frameHeight: 460,
    });
    this.load.spritesheet('zombie_shambler_death', 'assets/sprites/gameplay/zombie_shambler_death.png', {
      frameWidth: 310,
      frameHeight: 310,
    });

    // Load individual vehicle sprites (extracted from burned_vehicles spritesheet)
    this.load.image('vehicle_taxi', 'assets/tiles/vehicles_sprites/vehicle_taxi_0.png');
    this.load.image('vehicle_police_0', 'assets/tiles/vehicles_sprites/vehicle_police_0.png');
    this.load.image('vehicle_police_1', 'assets/tiles/vehicles_sprites/vehicle_police_1.png');
    this.load.image('vehicle_van', 'assets/tiles/vehicles_sprites/vehicle_van_0.png');

    // Load individual barricade sprites (extracted from barricades spritesheet)
    this.load.image('barricade_sandbags_0', 'assets/tiles/barricades_sprites/barricade_sandbags_0.png');
    this.load.image('barricade_sandbags_1', 'assets/tiles/barricades_sprites/barricade_sandbags_1.png');
    this.load.image('barricade_chair_0', 'assets/tiles/barricades_sprites/barricade_chair_0.png');
    this.load.image('barricade_concrete_0', 'assets/tiles/barricades_sprites/barricade_concrete_0.png');
    this.load.image('barricade_fence_0', 'assets/tiles/barricades_sprites/barricade_fence_0.png');
    this.load.image('barricade_drum_0', 'assets/tiles/barricades_sprites/barricade_drum_0.png');

    // Load individual debris sprites (extracted from urban_debris spritesheet)
    this.load.image('debris_dumpster_blue', 'assets/tiles/debris_sprites/debris_dumpster_blue.png');
    this.load.image('debris_dumpster_yellow', 'assets/tiles/debris_sprites/debris_dumpster_yellow.png');
    this.load.image('debris_trashcan_0', 'assets/tiles/debris_sprites/debris_trashcan_0.png');
    this.load.image('debris_hydrant_0', 'assets/tiles/debris_sprites/debris_hydrant_0.png');
    this.load.image('debris_mailbox_0', 'assets/tiles/debris_sprites/debris_mailbox_0.png');
    this.load.image('debris_newsstand_0', 'assets/tiles/debris_sprites/debris_newsstand_0.png');
    this.load.spritesheet('tile_trees', 'assets/tiles/dead_trees.png', { frameWidth: 32, frameHeight: 64 }); // Trees are often taller
    this.load.spritesheet('tile_blood', 'assets/tiles/blood_decals.png', { frameWidth: 32, frameHeight: 32 });
    this.load.spritesheet('tile_manholes', 'assets/tiles/manholes.png', { frameWidth: 32, frameHeight: 32 });
    this.load.spritesheet('tile_neon', 'assets/tiles/neon_signs.png', { frameWidth: 32, frameHeight: 32 });

    // Load helicopter sprites for airstrike
    this.load.image('helicopter_0', 'assets/sprites/vehicles/helicooter_0.png');
    this.load.image('helicopter_1', 'assets/sprites/vehicles/helicoopter_1.png');

    // Load sound effects
    this.load.audio('gunshot', 'assets/audio/sfx/Gun19.wav');
    this.load.audio('impact', 'assets/audio/sfx/Impact7.wav');

    // Load homing rocket weapon sprites
    this.load.image('homing_rocket', 'assets/sprites/weapons/weapon_homing-rocket_1_0.png');
    this.load.image('explosion_1', 'assets/sprites/weapons/effect_explosion_1_0.png');
    this.load.image('explosion_2', 'assets/sprites/weapons/effect_explosion_1_1.png');
    this.load.image('explosion_3', 'assets/sprites/weapons/effect_explosion_1_2.png');
    this.load.image('explosion_4', 'assets/sprites/weapons/effect_explosion_1_3.png');
    this.load.image('shell_icon', 'assets/sprites/weapons/weapon_shell-icon_1_0.png');
    this.load.image('rocket_icon', 'assets/sprites/weapons/weapon_rocket-icon_1_0.png');
    this.load.image('rocket_pickup', 'assets/sprites/weapons/pickup_rocket-ammo_1_0.png');

    // ==================== LEVEL 2 ASSETS ====================
    // Load industrial building sprites (generation 3 - Level 2 Power Plant)
    this.load.image('building_armory', 'assets/tiles/level2_sprites/buildings_industrial_3_sprites/building_armory_3_0.png');
    this.load.image('building_control_room', 'assets/tiles/level2_sprites/buildings_industrial_3_sprites/building_control-room_3_0.png');
    this.load.image('building_cooling_tower', 'assets/tiles/level2_sprites/buildings_industrial_3_sprites/building_cooling-tower_3_0.png');
    this.load.image('building_reactor_dome_a', 'assets/tiles/level2_sprites/buildings_industrial_3_sprites/building_reactor-dome_3_0.png');
    this.load.image('building_reactor_dome_b', 'assets/tiles/level2_sprites/buildings_industrial_3_sprites/building_reactor-dome_3_1.png');
    this.load.image('building_factory_a', 'assets/tiles/level2_sprites/buildings_industrial_3_sprites/building_factory_3_0.png');
    this.load.image('building_factory_b', 'assets/tiles/level2_sprites/buildings_industrial_3_sprites/building_factory_3_1.png');
    this.load.image('building_warehouse', 'assets/tiles/level2_sprites/buildings_industrial_3_sprites/building_warehouse_3_0.png');

    // Perimeter wall and gate sprites (processed with transparent backgrounds)
    this.load.image('wall_concrete', 'assets/sprites/buildings/wall_concrete_topdown_0_0_0.png');
    this.load.image('gate_security', 'assets/sprites/buildings/gate_security_0_0_0.png');
    this.load.image('gate_security_destroyed', 'assets/sprites/buildings/gate_security_destroyed_0_0_1.png');

    // Load Level 2 music - plays after gate breach
    this.load.audio('level2_breach_music', 'assets/audio/music/Level 2 - Ready to Boom (longer).mp3');

    // Load Animated Credits Scene sprites
    // Soldier run: 8 frames at 300x360 each = 2400x360
    this.load.spritesheet('credits_soldier_run', 'assets/sprites/credits/soldier_run.png', {
      frameWidth: 300,
      frameHeight: 360,
    });
    // Soldier shoot: 8 frames at 380x350 each = 3040x350
    this.load.spritesheet('credits_soldier_shoot', 'assets/sprites/credits/soldier_shoot.png', {
      frameWidth: 380,
      frameHeight: 350,
    });
    // Zombie walk: 6 frames at 230x310 each = 1380x310
    this.load.spritesheet('credits_zombie_walk', 'assets/sprites/credits/zombie_walk.png', {
      frameWidth: 230,
      frameHeight: 310,
    });
    // Zombie explode: 10 frames at 330x340 each = 3300x340
    this.load.spritesheet('credits_zombie_explode', 'assets/sprites/credits/zombie_explode.png', {
      frameWidth: 330,
      frameHeight: 340,
    });
    // Muzzle flash: 4 frames at 330x330 each = 1320x330
    this.load.spritesheet('credits_muzzle', 'assets/sprites/credits/muzzle_flash.png', {
      frameWidth: 330,
      frameHeight: 330,
    });
    // Bullet (single image)
    this.load.image('credits_bullet', 'assets/sprites/credits/bullet.png');

    // Create placeholder for zombie (still needed until we get zombie sprites)
    this.createPlaceholderAssets();
  }

  createPlaceholderAssets() {
    // Create placeholder for zombie
    const zombieGraphics = this.make.graphics({ x: 0, y: 0, add: false });
    zombieGraphics.fillStyle(0x556b2f, 1);
    zombieGraphics.fillRect(0, 0, 32, 32);
    zombieGraphics.fillStyle(0x8b0000, 1);
    zombieGraphics.fillRect(8, 8, 16, 16);
    zombieGraphics.generateTexture('zombie', 32, 32);
    zombieGraphics.destroy();

    // Create placeholder bullet (muzzle flash)
    const bulletGraphics = this.make.graphics({ x: 0, y: 0, add: false });
    bulletGraphics.fillStyle(0xffff00, 1);
    bulletGraphics.fillCircle(4, 4, 4);
    bulletGraphics.generateTexture('bullet', 8, 8);
    bulletGraphics.destroy();
  }

  create() {
    // Transition to title scene after short delay (for effect)
    // TitleScene gets user interaction first, which unlocks audio for MenuScene
    this.time.delayedCall(500, () => {
      this.scene.start('TitleScene');
    });
  }
}
